import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Radio } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { track } from "../../lib/tracking";
import { usePoll } from "../../lib/usePoll";
import { PATHS, fmtDate, useLang, useT } from "../../lib/i18n";
import { Button } from "../ui/button";

const RES = { won: ["WON", "var(--win)"], lost: ["LOST", "var(--loss)"], void: ["VOID", "#8a96a3"], half_won: ["½ WON", "var(--win)"], half_lost: ["½ LOST", "var(--loss)"] };

const Stake = ({ n }) => (
  <span className="flex gap-0.5" title={`${n} ${n === 1 ? "Unit" : "Units"}`}>{[1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-1.5 w-3 rounded-full ${i <= n ? "bg-[var(--ball)]" : "bg-border"}`} />)}</span>
);

export function TipCard({ tip, onDetails }) {
  const lang = useLang();
  const t = useT();
  const { user } = useAuth();
  const res = RES[tip.result];
  const live = tip.live;
  useEffect(() => {
    if (tip.is_member && !tip.locked && !sessionStorage.getItem(`mtv_${tip.id}`)) { sessionStorage.setItem(`mtv_${tip.id}`, "1"); track("member_tip_view", { tip_id: tip.id }); }
  }, [tip.id, tip.is_member, tip.locked]);
  return (
    <article className="surface surface-hover relative overflow-hidden p-5" data-testid={`tip-card-${tip.id}`}>
      <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground">
        <span className="truncate">{tip.tournament_url ? <Link to={tip.tournament_url.replace(/^\/de\//, `/${lang}/`)} className="hover:text-foreground" data-testid={`tip-tournament-link-${tip.id}`}>{tip.tournament_label || tip.tournament_name}</Link> : tip.tournament_label || tip.tournament_name || tip.tour} · {fmtDate(tip.start_time, lang)}</span>
        <span className="flex shrink-0 items-center gap-2">
          {tip.is_free && <span className="rounded-full bg-[var(--ball)]/15 px-2 py-0.5 font-semibold text-[var(--ball)]">FREE</span>}
          {tip.is_member && !tip.is_free && <span className="rounded-full border border-[var(--ball)]/50 px-2 py-0.5 font-semibold text-[var(--ball)]" data-testid="tip-member-badge">MEMBER</span>}
          {tip.is_grand_slam && <span className="rounded-full border border-border px-2 py-0.5">GS</span>}
          {res && <span className="rounded-full px-2 py-0.5 font-bold text-[#07090c]" style={{ background: res[1] }} data-testid="tip-result">{res[0]}</span>}
        </span>
      </div>
      <h3 className="mt-3 font-display text-2xl font-bold leading-tight">{tip.home} <span className="text-muted-foreground">vs</span> {tip.away}</h3>
      {live && (
        <p className="mt-2 flex items-center gap-2 font-num text-sm text-[var(--win)]" data-testid="tip-live-score"><Radio size={14} className="live-dot" />LIVE {live.ss || ""} {live.points ? `(${live.points})` : ""}</p>
      )}
      {tip.final_score && !live && <p className="mt-1 font-num text-sm text-muted-foreground">{tip.final_score}</p>}
      <div className="relative mt-4">
        <div className={`grid grid-cols-3 gap-3 rounded-xl bg-secondary/60 p-3 ${tip.locked ? "locked-blur" : ""}`}>
          <div className="col-span-2"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{tip.market}</p><p className="font-semibold" data-testid="tip-pick">{tip.locked ? "VIP Pick xxxxx" : tip.pick}</p></div>
          <div className="text-right"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Quote", "Odds")}</p><p className="font-num text-xl font-bold text-[var(--ball)]">{tip.locked ? "1.xx" : tip.odds}</p></div>
        </div>
        {tip.locked && (
          <Link to={user ? PATHS[lang].vip : PATHS[lang].register} onClick={() => track("vip_teaser_click", { tip_id: tip.id, member: !!user, tier: tip.is_member ? "member" : "vip" })} className="absolute inset-0 flex items-center justify-center px-2" data-testid="tip-unlock">
            <span className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-center text-sm font-semibold text-primary-foreground shadow-lg"><Lock size={14} className="shrink-0" />{user ? t("VIP freischalten", "Unlock VIP") : tip.is_member ? t("Kostenlos registrieren & Tipp sehen", "Join free & see this tip") : t("Kostenlos registrieren & mehr sehen", "Join free & unlock more")}</span>
          </Link>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-2">{t("Einsatz", "Stake")} <Stake n={tip.stake || 1} /></span>
        {!tip.locked && tip.bookmaker && <span>{tip.bookmaker}</span>}
        {typeof tip.profit === "number" && tip.status === "settled" && <span className="font-num font-bold" style={{ color: tip.profit >= 0 ? "var(--win)" : "var(--loss)" }}>{tip.profit > 0 ? "+" : ""}{tip.profit}u</span>}
      </div>
      {!tip.locked && tip.reasoning && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{tip.reasoning}</p>}
      {onDetails && !tip.locked && tip.betsapi_event_id && <Button variant="ghost" size="sm" className="mt-2 px-0 text-[var(--ball)]" onClick={() => onDetails(tip)} data-testid={`tip-details-${tip.id}`}>H2H & {t("Quoten", "Odds")} →</Button>}
    </article>
  );
}

export function TipGrid({ tips, empty, onDetails }) {
  const [live, setLive] = useState({});
  const t = useT();
  const ids = tips.filter((x) => x.status === "published" && x.betsapi_event_id && new Date(x.start_time) < new Date()).map((x) => x.betsapi_event_id).join(",");
  const load = useCallback(() => { if (ids) api.get(`/live?ids=${ids}`).then((r) => setLive(r.data)).catch(() => {}); }, [ids]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 30000, !!ids);
  if (!tips?.length) return <p className="surface p-8 text-center text-muted-foreground" data-testid="tips-empty">{empty || t("Aktuell keine Tipps – schau bald wieder vorbei.", "No tips right now – check back soon.")}</p>;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="tip-grid">
      {tips.map((x) => <TipCard key={x.id} tip={{ ...x, live: live[x.betsapi_event_id] || x.live }} onDetails={onDetails} />)}
    </div>
  );
}
